import { supabase } from '../lib/supabase';
import { logger } from './logger';
import { facturaCuentaComoVendida } from '../shared/pedido/facturaCuentaComoVendida';

export interface ProductoVentaStats {
  /** Última fecha con ítem en facturas completadas (12 m) */
  ultimaVentaFecha: string | null;
  /** Unidades vendidas en facturas con venta_completada (12 m). No suma la tabla `ventas`. */
  unidades12m: number;
  /** Total COP en líneas de facturas completadas (12 m) */
  montoVendido12m: number;
}

function parseItems(raw: unknown): any[] {
  if (raw == null) return [];
  if (typeof raw === 'string') {
    try {
      const p = JSON.parse(raw);
      return Array.isArray(p) ? p : [];
    } catch {
      return [];
    }
  }
  return Array.isArray(raw) ? raw : [];
}

function itemProductoId(item: any): string | null {
  const id = item?.productoId ?? item?.producto_id;
  return typeof id === 'string' && id.length > 0 ? id : null;
}

function itemCantidad(item: any): number {
  const c = item?.cantidad ?? item?.qty ?? 1;
  const n = Number(c);
  if (!Number.isFinite(n) || n <= 0) return 0;
  return Math.floor(n);
}

/** Subtotal de la línea en COP (subtotal/total o precio×cantidad). */
function itemMontoLinea(item: any): number {
  const sub = Number(item?.subtotal);
  if (Number.isFinite(sub) && sub > 0) return Math.round(sub);
  const tot = Number(item?.total);
  if (Number.isFinite(tot) && tot > 0) return Math.round(tot);
  const pu = Number(
    item?.precioUnitario ?? item?.precio_unitario ?? item?.precio ?? 0
  );
  const q = itemCantidad(item);
  if (Number.isFinite(pu) && q > 0) return Math.round(pu * q);
  return 0;
}

function docFecha(row: { fecha?: string | null; created_at?: string | null }): Date | null {
  const s = row.fecha || row.created_at;
  if (!s) return null;
  const d = new Date(s);
  return Number.isNaN(d.getTime()) ? null : d;
}

function startOfDayUtc(d: Date): Date {
  return new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate()));
}

/**
 * Ritmo diario a partir de unidades vendidas en 12 meses.
 */
export function ritmoDiarioVentas(unidades12m: number): number {
  return unidades12m / 365;
}

/**
 * Días aproximados para agotar el stock al ritmo de los últimos 12 meses.
 */
export function diasCoberturaStock(stockActual: number, unidades12m: number): number | null {
  const daily = ritmoDiarioVentas(unidades12m);
  if (daily <= 0) return null;
  if (stockActual <= 0) return 0;
  return Math.max(1, Math.round(stockActual / daily));
}

/**
 * Veces que “gira” el inventario en 12 m respecto al stock actual (referencia simple).
 */
export function rotacionInventarioRef(unidades12m: number, stockActual: number): number | null {
  if (stockActual <= 0) return unidades12m > 0 ? null : 0;
  if (unidades12m <= 0) return 0;
  return Math.round((unidades12m / stockActual) * 10) / 10;
}

/**
 * Agrega unidades vendidas desde `facturas` con `venta_completada` (últimos 12 meses).
 * No suma `ventas`: el checkout online escribe ambas filas y eso duplicaba el agregado
 * mientras el pedido seguía abierto.
 */
export async function fetchProductoVentasStatsMap(
  empresaId: string
): Promise<Record<string, ProductoVentaStats>> {
  const empty: Record<string, ProductoVentaStats> = {};
  if (!empresaId) return empty;

  const now = new Date();
  const cutoff = startOfDayUtc(now);
  cutoff.setUTCFullYear(cutoff.getUTCFullYear() - 1);
  const cutoffMs = cutoff.getTime();
  const cutoffStr = cutoff.toISOString().slice(0, 10);

  type Acc = { ultimaMs: number; unidades12m: number; monto12m: number };
  const acc = new Map<string, Acc>();

  const bump = (productoId: string, fechaMs: number, qty: number, montoLinea: number) => {
    let a = acc.get(productoId);
    if (!a) {
      a = { ultimaMs: 0, unidades12m: 0, monto12m: 0 };
      acc.set(productoId, a);
    }
    if (qty > 0) a.unidades12m += qty;
    if (montoLinea > 0) a.monto12m += montoLinea;
    if (fechaMs > a.ultimaMs) a.ultimaMs = fechaMs;
  };

  const processDoc = (
    row: {
      fecha?: string | null;
      created_at?: string | null;
      items?: unknown;
      estado?: string | null;
      estado_pago?: string | null;
      venta_completada?: boolean | null;
    }
  ) => {
    if (!facturaCuentaComoVendida(row)) return;
    const fd = docFecha(row);
    if (!fd) return;
    const ms = fd.getTime();
    if (ms < cutoffMs) return;

    const items = parseItems(row.items);
    for (const it of items) {
      const pid = itemProductoId(it);
      if (!pid) continue;
      const qty = itemCantidad(it);
      const monto = itemMontoLinea(it);
      bump(pid, ms, qty, monto);
    }
  };

  try {
    const facturasRes = await supabase
      .from('facturas')
      .select('fecha,items,estado,estado_pago,venta_completada,created_at')
      .eq('empresa_id', empresaId)
      .eq('venta_completada', true)
      .gte('fecha', cutoffStr);

    if (facturasRes.error) {
      logger.warn('productoVentasStats: facturas', facturasRes.error.message);
    } else {
      (facturasRes.data || []).forEach((row) => processDoc(row));
    }
  } catch (e) {
    logger.error('productoVentasStats:', e);
    return empty;
  }

  const out: Record<string, ProductoVentaStats> = {};
  acc.forEach((v, productoId) => {
    out[productoId] = {
      ultimaVentaFecha:
        v.ultimaMs > 0 ? new Date(v.ultimaMs).toISOString().slice(0, 10) : null,
      unidades12m: v.unidades12m,
      montoVendido12m: v.monto12m,
    };
  });

  return out;
}
